import { matchPath } from 'react-router-dom'

interface IPathObject {
  admin: {
    organizations: Record<
      'root' | 'reports' | 'settings' | 'users' | 'activities' | 'groups',
      string
    >
    integrations: Record<'root' | 'accounts' | 'targets', string>
    elements: Record<'root' | 'patterns' | 'lists' | 'fieldsets', string>
    templates: Record<'root' | 'templates' | 'folders', string>
  }
  complianceDashboard: string
  datasets: string
  submissions: {
    root: string
    pending: string
    approvals: string
  }
  reports: string
  taxonomyTranslation: string
  taxonomyUpload: string
  taxonomyDetails: string
}

export const URLS: IPathObject = {
  complianceDashboard: '/dashboard',
  datasets: '/',
  submissions: {
    root: '/codes/submissions',
    pending: '/codes/pending',
    approvals: '/codes/approval',
  },
  reports: '/reports',
  taxonomyTranslation: '/admin/taxonomy-translation',
  taxonomyUpload: '/admin/taxonomy-translation/create',
  taxonomyDetails: '/admin/taxonomy-translation/:taxonomyId',
  admin: {
    organizations: {
      root: '/admin/account/reports',
      reports: '/admin/account/reports',
      settings: '/admin/account/settings',
      users: '/admin/users',
      activities: '/admin/activities',
      groups: '/admin/groups',
    },
    integrations: {
      root: '/admin/accounts',
      accounts: '/admin/accounts',
      targets: '/admin/targets',
    },
    elements: {
      root: '/admin/fieldsets',
      fieldsets: '/admin/fieldsets',
      patterns: '/admin/patterns',
      lists: '/admin/lists',
    },
    templates: {
      root: '/admin/templates',
      templates: '/admin/templates',
      folders: '/admin/folders',
    },
  },
}

type PathType = IPathObject | Record<string, any> | string

const permissionToKeyMap = {
  access_reports: 'reports',
  manage_account_settings: 'settings',
  manage_connectors: 'accounts',
  manage_users: 'users',
  manage_groups: 'groups',
  manage_targets: 'targets',
  manage_field_sets: 'fieldsets',
  manage_patterns: 'patterns',
  manage_lists: 'lists',
  manage_templates: 'templates',
  manage_folders: 'folders',
}

export function hasPermission(permissions: string[] | undefined, permission: string): boolean {
  return permissions?.includes('all_privileges') || permissions?.includes(permission) || false
}

export function hasAnyPermission(permissions: string[] | undefined, permission: string[]): boolean {
  return (
    permissions?.includes('all_privileges') ||
    permissions?.some(p => permission.includes(p)) ||
    false
  )
}

export function findNextAvailablePath(
  pathObj: Record<string, string>,
  permissions: string[] | undefined = [],
): string | null {
  const paths = Object.entries(pathObj).filter(([key]) => key !== 'root')
  for (const [key, path] of paths) {
    const requiredPermission = Object.entries(permissionToKeyMap).find(([_, k]) => {
      if (Array.isArray(k)) {
        return k.find(kp => kp === key)
      }
      return k === key
    })?.[0]

    if (requiredPermission && hasPermission(permissions, requiredPermission)) {
      return path
    }
  }
  return null
}

type MatchPathReturn = { [key: string]: any; isExact: boolean } | null

const isActive = (location, pathObj: PathType, key: string | null): boolean => {
  const currentPath = key ? (typeof pathObj === 'string' ? pathObj : pathObj[key]) : pathObj
  const isCurrentPath = (path: string): MatchPathReturn =>
    matchPath(location.pathname, {
      path,
      exact: true,
      strict: true,
    })

  if (key && key !== 'root' && typeof pathObj === 'object') {
    // Gets '/admin/account/reports', '/admin/account/settings', etc
    const fullPath = pathObj[key]
    return isCurrentPath(fullPath)?.isExact ?? false
  }

  if (key === 'root') {
    if (isCurrentPath(currentPath)?.isExact) return true
    return Object.values(pathObj).some((childPath: any) => {
      if (typeof childPath === 'string') {
        return isCurrentPath(childPath)?.isExact
      }
      return false
    })
  }

  // Direct path comparison
  if (typeof currentPath === 'string') {
    return isCurrentPath(currentPath)?.isExact ?? false
  }

  return false
}

function getMenuPath(pathObj: Record<string, any>, permissions: string[] | undefined = []): string {
  if (!pathObj?.root) return Object.values(pathObj)[0]!

  const nextPath = findNextAvailablePath(pathObj, permissions)
  return nextPath ?? pathObj.root
}

interface IMenuPropsReturn {
  to: string
  active: boolean
}

export function menuProps(
  location,
  pathObj: PathType,
  pathname: string | null,
  permissions?: string[],
): IMenuPropsReturn {
  if (typeof pathObj === 'string') {
    return {
      to: pathObj,
      active: isActive(location, pathObj, pathname),
    }
  }

  if (pathname === 'root') {
    const path = getMenuPath(pathObj as Record<string, any>, permissions)
    return {
      to: path,
      active: isActive(location, pathObj, pathname),
    }
  }

  // For non-root paths, verify the path exists in object
  // For nested menu items, pathname should match one of the paths in pathObj
  const fullPath = (pathObj as Record<string, any>)[pathname!]
  return {
    to: fullPath || (pathObj as Record<string, any>).root,
    active: isActive(location, pathObj, pathname),
  }
}
